/* eslint-disable react-hooks/exhaustive-deps */
import { checkGroundingService, getGroundingConfigurationByVIN } from 'apis/groundingApis'
import { SessionStorageKey, VIN_CHARACTERS_REGEX } from 'common/constants'
import { CustomLoader } from 'components/Loader'
import { Formik } from 'formik'
import { useEffect, useState, useRef } from 'react'
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { useGlobalStore } from 'store/useGlobalStore'
import { IVinSearchForm, initialVinValues } from 'types/Grounding/groundingTypes'
import * as Yup from 'yup'
import shallow from 'zustand/shallow'
import { StyledGroundingWithVIN } from './GroundingWithVIN.styled'
import { GroundingRenderStep } from './store/RenderStep'
import { useGroundingActionStore } from './store/useGroundingActionStore'
import { checkCamera } from 'utils/cameraUtils'
import BarcodeScannerIcon from 'icons/BarcodeScannerIcon'
import { AlertDialog } from 'components/AlertDialog/AlertDialog'
import ScanVIN from 'components/Grounding/ScanVIN'
import { useGroundingStore } from './store/useGroundingStore'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'

const schema = Yup.object({
  VIN: Yup.string().length(17, 'Must be 17 characters').required('Required')
})

export interface IProps {
  setLocationSelected?: (locationID: number) => void
  handleSetDefaultGroundingData?: (data) => void
}

export const GroundingWithVIN = () => {
  const { getFeatureToggle } = useGlobalStore()
  const { userInteraction } = useDtmAnalytics()
  const showGroundingPageLoadingBar = getFeatureToggle('GroundingPageLoadingBar')
  const [setGroundingData, setDefaultGroundingData, setAlreadyConfirmRecall, setAlreadyConfirmGeneralModal] =
    useGroundingStore(
      (state) => [
        state.setGroundingData,
        state.setDefaultGroundingData,
        state.setAlreadyConfirmRecall,
        state.setAlreadyConfirmGeneralModal
      ],
      shallow
    )

  const [setActiveStep] = useGroundingActionStore((state) => [state.setActiveStep], shallow)

  const [showMessage, setShowMessage] = useState(false)
  const [isDefiBlocked, setIsDefiBlocked] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasAlreadyChanged, setHasAlreadyChanged] = useState<boolean>(false)
  const [modalShow, setModalShow] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const scanButtonRef = useRef<HTMLButtonElement>(null)

  const getLocalText = useGlobalStore((state) => state.getLocalText)

  const handleSearchGrounding = (values: IVinSearchForm) => {
    if (values?.VIN) {
      userInteraction('Grounding: Ground Vehicle', [{ VIN: values.VIN }])
      getInitialGroundingData(values?.VIN)
    }
  }

  const handleScanComplete = (scannedVin: string | null) => {
    setShowScanner(false)
    if (scannedVin) {
      initialVinValues.VIN = scannedVin
      getInitialGroundingData(scannedVin)
      setHasAlreadyChanged(true)
    }
  }

  const redirectToCamera = async () => {
    try {
      setCameraError('')
      const hasCamera = await checkCamera()

      if (hasCamera) {
        // On mobile, it's better to have a small delay before showing the scanner
        // This helps with the fullscreen API which often requires a user gesture
        setTimeout(() => {
          setShowScanner(true)
        }, 100)
      } else {
        setCameraError('No camera detected or permission denied')
        setModalShow(true)
      }
    } catch (error) {
      console.error('Camera access error:', error)
      setCameraError('Error accessing camera: ' + (error.message || 'Unknown error'))
      setModalShow(true)
    }
  }

  useEffect(() => {
    setAlreadyConfirmRecall(false)
    setAlreadyConfirmGeneralModal(false)
    setGroundingData(null)
    let isServiceRunning = checkGroundingService()
    if (!isServiceRunning) {
      setErrorMessage(
        'We are currently experiencing technical difficulties with the grounding process.  Please try back again in a few minutes. We apologize for the inconvenience.'
      )
      setShowMessage(true)
      return
    }
    let VIN = sessionStorage.getItem(SessionStorageKey.GROUND_VIN)
    if (VIN !== null && VIN !== '') {
      sessionStorage.setItem(SessionStorageKey.GROUND_VIN, '')
      initialVinValues.VIN = VIN
      setHasAlreadyChanged(true)
      getInitialGroundingData(VIN)
    }
  }, [])

  const getInitialGroundingData = async (VIN: string) => {
    try {
      setIsLoading(true)
      const response = await getGroundingConfigurationByVIN(VIN)

      if (response) {
        if ((response.ErrorMessage !== undefined && response.ErrorMessage !== '') || response.SavePayoffError) {
          if (response.IsDefiBlocked) {
            setIsDefiBlocked(true)
          }
          setErrorMessage(response.ErrorMessage ?? response.SavePayoffError)
          setShowMessage(true)
          return
        }
        response.GroundingNotice = response.GroundingNotice?.replace(
          '<a href="#terms-modal" data-toggle="modal">Terms and Conditions</a>',
          '<a href="" id="termsLink" >Terms and Conditions</a>'
        )
        response.vehicleReturnDate = null
        initialVinValues.VIN = VIN
        setGroundingData(response)
        setDefaultGroundingData(response)
        setActiveStep(GroundingRenderStep.GRD_OPTIONS)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <StyledGroundingWithVIN>
      <AlertDialog
        title="Camera Access Error"
        description={cameraError || 'Please allow camera access to use this feature'}
        show={modalShow}
        onHide={() => setModalShow(false)}
        isConfirmation={false}
      />
      {showScanner && <ScanVIN onComplete={handleScanComplete} />}
      <Row>
        <Col>
          <Formik
            enableReinitialize
            initialValues={initialVinValues}
            onSubmit={(values) => handleSearchGrounding(values)}
            validationSchema={schema}
            onReset={() => {
              setShowMessage(false)
              setIsDefiBlocked(false)
              initialVinValues.VIN = ''
            }}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit} onReset={props.handleReset}>
                <Row style={{ marginTop: '20px' }}>
                  <Form.Group className="col-lg-4 col-sm-8" controlId="VIN">
                    <Row>
                      <Form.Label column sm="auto">
                        <h5>Enter VIN</h5>
                      </Form.Label>
                      <Col className="p-0">
                        <InputGroup>
                          <Form.Control
                            type="input"
                            placeholder="#17 digit VIN"
                            onChange={(e) => {
                              if (e.target.value !== props.values.VIN) {
                                setShowMessage(false)
                                setIsDefiBlocked(false)
                              }
                              props.handleChange(e)
                              setHasAlreadyChanged(true)
                            }}
                            onBlur={props.handleBlur}
                            value={props.values.VIN}
                            isInvalid={hasAlreadyChanged && !!props.errors.VIN}
                            onKeyDown={(e) => {
                              if (!VIN_CHARACTERS_REGEX.test(e.key)) {
                                e.preventDefault()
                              }
                            }}
                          />
                          <InputGroup.Append>
                            <Button
                              className="search-option-scan-btn"
                              variant=""
                              onClick={redirectToCamera}
                              ref={scanButtonRef}
                            >
                              <BarcodeScannerIcon />
                              <label>SCAN</label>
                            </Button>
                          </InputGroup.Append>
                        </InputGroup>

                        <Form.Control.Feedback tooltip={false} type="invalid">
                          {hasAlreadyChanged && props.errors.VIN}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  </Form.Group>
                  <Form.Group className="col-lg-4 col-sm-4 pl-0">
                    <Button type="submit" disabled={!props.isValid || isDefiBlocked} className="mx-2">
                      GROUND
                    </Button>
                    <Button className="btn-secondary" type="reset">
                      CLEAR ALL
                    </Button>
                  </Form.Group>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      {showGroundingPageLoadingBar === 1 && isLoading && (
        <Row className="spinner-row">{<CustomLoader size="97px" />}</Row>
      )}
      {!isLoading && (
        <Row>
          <Col lg={{ span: 4 }} sm={{ span: 8 }}>
            {showMessage && (
              <Alert variant="danger" onClose={() => setShowMessage(false)} dismissible>
                {getLocalText(errorMessage, errorMessage)}
              </Alert>
            )}
          </Col>
        </Row>
      )}
    </StyledGroundingWithVIN>
  )
}
